/**
 * What moved since last time (R13, ADR-022).
 *
 * Assessments have been dated and append-only since M1, and nothing has ever
 * read them back. "The delta over time is the product" was true as a claim and
 * false as a feature — the history was write-only.
 *
 * ── The thing this module exists to get right ──────────────────────────────
 *
 * A difference between two numbers is not a delta unless the two numbers
 * measured the same thing. Three ways that fails here, in descending severity:
 *
 *   1. The ROLE changed. Someone assessed as a Project Manager in March and a
 *      Product Manager in September has not seen their exposure fall by 1.4 —
 *      they have been measured against a different occupation. Subtracting is
 *      meaningless, and printing "↓ 1.4, good news" would be a lie with a
 *      number attached. The role change is itself the story.
 *
 *   2. The METHOD changed. A future scorer will not agree with this one, and a
 *      shift caused by our own algorithm is not a shift in the person's work.
 *
 *   3. PERSONALISATION changed. Same algorithm, but one run used the person's
 *      confirmed activities and the other used the flat occupation profile.
 *      Comparable — a real difference in the person's inputs — but it needs
 *      saying, because otherwise the user reads their own diligence as change
 *      in the labour market.
 *
 * (1) and (2) block the comparison. (3) annotates it. This is the same
 * principle as ADR-031's guards: being confidently wrong is worse than
 * declining to answer.
 */

/** Firestore Timestamp | ISO string | Date | null → Date | null */
export function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "deterministic-v1+confirmed" → { base: "deterministic-v1", personalised: true } */
export function parseMethod(method) {
  const [base, ...rest] = String(method || "").split("+");
  return { base: base || null, personalised: rest.includes("confirmed") };
}

const AXES = [
  ["digitality", "Digitality"],
  ["judgment", "Judgment"],
  ["coordination", "Coordination"],
  ["premium", "Premium"],
];

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * @param prior   a stored assessment document: { at, occupationCode, occupationTitle,
 *                exposure: { overall, digitality, ..., method, personalised } }
 * @param current the live score from `scoreProfile()`, plus the resolved occupation
 * @returns null when there is nothing to compare, otherwise a verdict
 */
export function compareAssessments(prior, current, currentMeta = {}) {
  if (!prior?.exposure || !current) return null;

  const p = prior.exposure;
  const priorOverall = p.overall;
  const currentOverall = current.exposure;
  if (typeof priorOverall !== "number" || typeof currentOverall !== "number") return null;

  const pm = parseMethod(p.method);
  const cm = parseMethod(current.method);

  const roleChanged =
    Boolean(prior.occupationCode && currentMeta.occupationCode) &&
    prior.occupationCode !== currentMeta.occupationCode;
  const methodChanged = Boolean(pm.base && cm.base) && pm.base !== cm.base;
  const personalisationChanged = pm.personalised !== cm.personalised;

  const comparable = !roleChanged && !methodChanged;

  return {
    when: toDate(prior.at),
    priorTitle: prior.occupationTitle || null,
    currentTitle: currentMeta.occupationTitle || null,
    roleChanged,
    methodChanged,
    personalisationChanged,
    comparable,
    // Present even when incomparable, so the caller can decide — but never
    // labelled as a change by this module.
    overall: { from: priorOverall, to: currentOverall, change: round1(currentOverall - priorOverall) },
    axes: AXES.map(([key, label]) => {
      const from = p[key];
      const to = current[key];
      return typeof from === "number" && typeof to === "number"
        ? { key, label, from, to, change: round1(to - from) }
        : null;
    }).filter(Boolean),
    reason: !comparable
      ? roleChanged
        ? "different-role"
        : "different-method"
      : personalisationChanged
        ? "different-personalisation"
        : null,
  };
}

/**
 * The most recent PRIOR assessment worth comparing against.
 *
 * `history` arrives newest-first. The current session's own record may already
 * be in there — the shell writes it as soon as the score exists — so anything
 * created in the last few minutes is skipped rather than compared against
 * itself, which would always read as "no change".
 */
export function mostRecentPrior(history, { now = Date.now(), graceMs = 5 * 60 * 1000 } = {}) {
  if (!Array.isArray(history)) return null;
  for (const h of history) {
    const at = toDate(h?.at);
    // A pending serverTimestamp() reads as null locally; treat it as "just now".
    if (!at) continue;
    if (now - at.getTime() < graceMs) continue;
    if (h?.exposure) return h;
  }
  return null;
}
